import useSWR from 'swr';

import {
    type InterruptResponse,
    type MessageCreateRequest,
    type QuestionAnswerRequest,
    type QuestionAnswerResponse,
    type ToolDecisionRequest,
    type ToolDecisionResponse,
} from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import {
    type CandidateRunScope,
    type RunSummary,
    type StreamRequest,
} from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { getApiClient } from '@apps/web/lib/api_client';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type ApiClient,
    ApiError,
    type components,
    type FetchResponse,
} from '@libraries/typescript/api_client';

const RUN_PATH = '/organizations/{organization_id}/runs/{run_id}';
const STREAM_PATH = `${RUN_PATH}/events/stream`;
const STREAM_MEDIA_TYPE = 'application/x-ndjson';
export class RunEventStreamProtocolError extends Error {
    public constructor(
        public readonly kind:
            | 'unexpected_status'
            | 'missing_body'
            | 'invalid_media_type'
            | 'redirect',
    ) {
        super('The run event stream response violated its protocol.');
        this.name = 'RunEventStreamProtocolError';
    }
}

/** Carries only the response fields the read client needs for recovery. */
export class RunEventStreamHttpError extends Error {
    public constructor(
        public readonly status: number,
        public readonly retryAfterMs: number | null,
    ) {
        super('The run event stream request failed.');
        this.name = 'RunEventStreamHttpError';
    }
}

export class RunApiError extends Error {
    /** True when the underlying request never reached the server. Together
     * with `status` this is the shape the global SWR toast classification
     * reads off translated errors (see `shouldShowSWRToastForError`),
     * carrying no customer-derived detail. */
    public readonly isNetworkError: boolean;

    public constructor(
        public readonly kind:
            | 'aborted'
            | 'network'
            | 'unauthorized'
            | 'forbidden'
            | 'not_found'
            | 'rejected'
            | 'unknown',
        /** The HTTP status this error was translated from, when one exists. */
        public readonly status?: number,
    ) {
        super('The run request failed.');
        this.name = 'RunApiError';
        this.isNetworkError = kind === 'network';
    }
}

export type RunSummaryState =
    | Readonly<{ readonly kind: 'loading' }>
    | Readonly<{ readonly kind: 'ready'; readonly summary: RunSummary }>;

export function createRun({
    activeModel = null,
    mode,
    organizationId,
    runType = null,
}: Readonly<{
    /** A platform admin's model pin, applied from the first turn. */
    readonly activeModel?: string | null;
    readonly mode: 'live' | 'sandbox';
    readonly organizationId: string;
    /** Null creates the general chat the API already defaults to. */
    readonly runType?: 'orchestrator' | null;
}>): Promise<Readonly<components['schemas']['AgentRunSummary']>> {
    return getApiClient().post({
        url: '/organizations/{organization_id}/runs',
        pathParams: { organization_id: organizationId },
        mode,
        reason: 'v3-sidebar-create-run',
        body: {
            title: 'New chat',
            ...(activeModel === null ? {} : { active_model: activeModel }),
            ...(runType === null ? {} : { run_type: runType }),
        },
    });
}

/** The chat an outreach was started from, when Phoebe created it in one. */
export function useOutreachOriginRun({
    mode,
    organizationId,
    outreachId,
}: Readonly<{
    readonly mode: 'live' | 'sandbox';
    readonly organizationId: string;
    readonly outreachId: string | null;
}>): Readonly<{ readonly id: string; readonly title: string | null }> | null {
    const origin = useApiQuery({
        path: '/organizations/{organization_id}/runs/by-resource',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: {
            resource_type: 'outreach',
            resource_id: outreachId ?? '',
            role: 'related',
        },
        enabled: outreachId !== null,
        mode,
        reason: 'v3-sidebar-outreach-origin',
        select: (run) => ({ id: run.id, title: run.title }),
    });
    return origin.data ?? null;
}

export function ensureOutreachRunnerRun({
    mode,
    organizationId,
    outreachId,
}: Readonly<{
    readonly mode: 'live' | 'sandbox';
    readonly organizationId: string;
    readonly outreachId: string;
}>): Promise<RunSummary> {
    return getApiClient().post({
        url: '/organizations/{organization_id}/runs/outreach-runner/by-outreach/{outreach_id}',
        pathParams: {
            organization_id: organizationId,
            outreach_id: outreachId,
        },
        mode,
        reason: 'v3-sidebar-ensure-outreach-runner',
    });
}

type DeadlineClock = Readonly<{
    readonly schedule: (callback: () => void, delayMs: number) => number;
    readonly cancel: (handle: number) => void;
}>;

type RunApiDependencies = Readonly<{
    readonly getClient: () => ApiClient;
    readonly clock: DeadlineClock;
}>;

type ServerQuestionAnswerRequest = Readonly<
    components['schemas']['AgentRunQuestionAnswerRequest']
>;
type ServerToolDecisionRequest = Readonly<
    components['schemas']['AgentRunToolDecisionRequest']
>;

function requireCompletePair(
    first: string | null,
    second: string | null,
): void {
    if ((first === null) !== (second === null)) {
        throw new RunApiError('rejected');
    }
}

function copyAnswer(
    answer: string | ReadonlyArray<string> | null | undefined,
): string | Array<string> | null | undefined {
    if (typeof answer === 'string' || answer === null || answer === undefined) {
        return answer;
    }
    return Array.from(answer);
}

function requestScope(scope: StreamRequest['scope'], signal: AbortSignal) {
    return {
        pathParams: {
            organization_id: scope.organizationId,
            run_id: scope.runId,
        },
        mode: scope.mode,
        signal,
    } as const;
}

function mapMutationFailure(error: unknown): RunApiError {
    if (!(error instanceof ApiError)) {
        return new RunApiError('unknown');
    }
    if (error.isAbortError) {
        return new RunApiError('aborted');
    }
    if (error.isNetworkError) {
        return new RunApiError('network');
    }
    const kind =
        error.status === 401
            ? 'unauthorized'
            : error.status === 403
              ? 'forbidden'
              : error.status === 404
                ? 'not_found'
                : error.status !== undefined && error.status < 500
                  ? 'rejected'
                  : 'unknown';
    return new RunApiError(kind, error.status);
}

function finiteMutationFailure(error: unknown): Readonly<{
    readonly kind: 'definitively_rejected' | 'ambiguous';
}> {
    if (
        error instanceof ApiError &&
        !error.isAbortError &&
        !error.isNetworkError &&
        error.status !== undefined &&
        error.status < 500
    ) {
        return { kind: 'definitively_rejected' };
    }
    return { kind: 'ambiguous' };
}

async function translateApiFailure<Value>(
    mutation: () => Promise<Value>,
): Promise<Value> {
    try {
        return await mutation();
    } catch (error: unknown) {
        throw mapMutationFailure(error);
    }
}

function parseRetryAfterMs(response: FetchResponse | undefined): number | null {
    const retryAfter = response?.headers.get('retry-after')?.trim();
    if (retryAfter === undefined || !/^\d+$/.test(retryAfter)) {
        return null;
    }
    const retryAfterSeconds = Number(retryAfter);
    if (!Number.isFinite(retryAfterSeconds)) {
        return Number.MAX_SAFE_INTEGER;
    }
    return Math.min(retryAfterSeconds * 1_000, Number.MAX_SAFE_INTEGER);
}

function isRedirectResponse(response: FetchResponse): boolean {
    return (
        response.redirected ||
        response.type === 'opaqueredirect' ||
        (response.status >= 300 && response.status < 400)
    );
}

function validateStreamResponse(
    response: FetchResponse,
    streamSettled: () => void,
): Response {
    if (isRedirectResponse(response)) {
        streamSettled();
        throw new RunEventStreamProtocolError('redirect');
    }
    if (response.status >= 400) {
        streamSettled();
        throw new RunEventStreamHttpError(
            response.status,
            parseRetryAfterMs(response),
        );
    }
    if (response.status !== 200) {
        streamSettled();
        throw new RunEventStreamProtocolError('unexpected_status');
    }
    if (response.body === null) {
        streamSettled();
        throw new RunEventStreamProtocolError('missing_body');
    }
    const mediaType = response.headers
        .get('content-type')
        ?.split(';', 1)[0]
        ?.trim()
        .toLowerCase();
    if (mediaType !== STREAM_MEDIA_TYPE) {
        streamSettled();
        throw new RunEventStreamProtocolError('invalid_media_type');
    }
    try {
        const reader = response.body.getReader();
        const body = new ReadableStream<Uint8Array>({
            async pull(controller): Promise<void> {
                try {
                    const result = await reader.read();
                    if (result.done) {
                        streamSettled();
                        controller.close();
                        return;
                    }
                    controller.enqueue(result.value);
                } catch (error: unknown) {
                    streamSettled();
                    controller.error(error);
                }
            },
            async cancel(reason: unknown): Promise<void> {
                streamSettled();
                await reader.cancel(reason);
            },
        });
        return new Response(body, {
            headers: response.headers,
            status: response.status,
            statusText: response.statusText,
        });
    } catch (error: unknown) {
        streamSettled();
        throw error;
    }
}

export function createRunApi(dependencies: RunApiDependencies) {
    async function openEventStream(request: StreamRequest): Promise<Response> {
        const { cursor } = request;
        requireCompletePair(
            cursor.after_pending_created_at,
            cursor.after_pending_id,
        );
        requireCompletePair(
            cursor.after_pending_status_updated_at,
            cursor.after_pending_status_id,
        );

        const queryParams = {
            after_offset: cursor.after_offset,
            ...(cursor.after_pending_created_at === null
                ? {}
                : {
                      after_pending_created_at: cursor.after_pending_created_at,
                      after_pending_id: cursor.after_pending_id,
                  }),
            ...(cursor.after_pending_status_updated_at === null
                ? {}
                : {
                      after_pending_status_updated_at:
                          cursor.after_pending_status_updated_at,
                      after_pending_status_id: cursor.after_pending_status_id,
                  }),
            include_snapshot: request.includeSnapshot,
            max_events: request.maxEventCount,
            max_pending_events: request.maxPendingEventCount,
            max_pending_status_updates: request.maxPendingStatusUpdateCount,
            wait_ms: request.waitMs,
        };
        // Use one controller for both abort sources; Safari <17.4 has no any().
        const streamController = new AbortController();
        const deadlineHandle = dependencies.clock.schedule(
            () => streamController.abort(),
            35_000,
        );
        let deadlineActive = true;
        const cancelDeadline = (): void => {
            if (!deadlineActive) {
                return;
            }
            deadlineActive = false;
            dependencies.clock.cancel(deadlineHandle);
            request.signal.removeEventListener('abort', abortRequest);
        };
        const abortRequest = (): void => {
            streamController.abort(request.signal.reason);
            cancelDeadline();
        };
        request.signal.addEventListener('abort', abortRequest, {
            once: true,
        });
        if (request.signal.aborted) {
            abortRequest();
        }
        const signal = streamController.signal;

        const rawRequest = {
            method: 'GET',
            url: STREAM_PATH,
            pathParams: {
                organization_id: request.scope.organizationId,
                run_id: request.scope.runId,
            },
            queryParams,
            headers: { Accept: STREAM_MEDIA_TYPE },
            mode: request.scope.mode,
            reason: 'v3-agent-run-event-stream',
            signal,
            cache: 'no-store',
            credentials: 'include',
            redirect: 'manual',
        } as const;
        // getClient() and raw() can throw synchronously, before any promise
        // exists, so .catch() alone would leak the deadline and its listener.
        const rawResponse = (() => {
            try {
                return dependencies.getClient().raw(rawRequest);
            } catch (error: unknown) {
                cancelDeadline();
                throw error;
            }
        })();
        const response = await rawResponse.catch((error: unknown): never => {
            cancelDeadline();
            if (
                error instanceof ApiError &&
                error.response !== undefined &&
                isRedirectResponse(error.response)
            ) {
                throw new RunEventStreamProtocolError('redirect');
            }
            if (error instanceof ApiError && error.status !== undefined) {
                throw new RunEventStreamHttpError(
                    error.status,
                    parseRetryAfterMs(error.response),
                );
            }
            throw error;
        });
        return validateStreamResponse(response, cancelDeadline);
    }

    async function getRunSummary(
        scope: CandidateRunScope,
    ): Promise<RunSummary> {
        const summary = await translateApiFailure(() =>
            dependencies.getClient().get({
                url: RUN_PATH,
                pathParams: {
                    organization_id: scope.organizationId,
                    run_id: scope.runId,
                },
                mode: scope.mode,
                reason: 'v3-agent-run-summary',
            }),
        );
        if (summary.id !== scope.runId) {
            throw new RunApiError('rejected');
        }
        return summary;
    }

    async function sendMessage(
        scope: StreamRequest['scope'],
        request: MessageCreateRequest,
        signal: AbortSignal,
    ) {
        return translateApiFailure(async () => {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/messages`,
                ...requestScope(scope, signal),
                body: {
                    content: request.content,
                    ...(request.ui_context === null ||
                    request.ui_context === undefined
                        ? {}
                        : {
                              ui_context: {
                                  ...request.ui_context,
                                  visible_row_ids: Array.from(
                                      request.ui_context.visible_row_ids,
                                  ),
                              },
                          }),
                    client_operation_id: request.client_operation_id,
                    ...(request.activity_event_ids === undefined
                        ? {}
                        : {
                              activity_event_ids: Array.from(
                                  request.activity_event_ids,
                              ),
                          }),
                    ...(request.upload_ids === undefined
                        ? {}
                        : { upload_ids: Array.from(request.upload_ids) }),
                },
            });
            return {
                run_id: response.run_id,
                turn_id: response.turn_id ?? null,
                queued: response.queued,
            };
        });
    }

    async function answerQuestion(
        scope: StreamRequest['scope'],
        request: ServerQuestionAnswerRequest,
        signal: AbortSignal,
    ) {
        return translateApiFailure(async () => {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/question-answers`,
                ...requestScope(scope, signal),
                body: {
                    call_id: request.call_id,
                    answer: copyAnswer(request.answer),
                    answers:
                        request.answers === null
                            ? null
                            : request.answers?.map((answer) => ({
                                  question_id: answer.question_id,
                                  answer: copyAnswer(answer.answer),
                                  skipped: answer.skipped,
                              })),
                    ...(request.message_preview_edit === null ||
                    request.message_preview_edit === undefined
                        ? {}
                        : {
                              message_preview_edit: {
                                  recipients: [
                                      ...request.message_preview_edit
                                          .recipients,
                                  ],
                                  message: request.message_preview_edit.message,
                              },
                          }),
                    cancelled: request.cancelled,
                },
            });
            return {
                call_id: response.call_id,
                already_resolved: response.already_resolved,
                resumed: response.resumed,
                cancelled: response.cancelled,
                run_id: response.run_id ?? null,
                queued: response.queued,
            };
        });
    }

    async function decideToolApproval(
        scope: StreamRequest['scope'],
        request: ServerToolDecisionRequest,
        signal: AbortSignal,
    ) {
        return translateApiFailure(async () => {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/tool-decisions`,
                ...requestScope(scope, signal),
                body: {
                    call_id: request.call_id,
                    call_ids:
                        request.call_ids === null ||
                        request.call_ids === undefined
                            ? request.call_ids
                            : [...request.call_ids],
                    decision: request.decision,
                    always_allow_scope: request.always_allow_scope,
                    arguments_override: request.arguments_override,
                },
            });
            return {
                call_id: response.call_id,
                call_ids: response.call_ids ?? [],
                decision: response.decision,
                already_resolved: response.already_resolved,
                resumed: response.resumed,
                run_id: response.run_id ?? null,
                queued: response.queued,
            };
        });
    }

    async function interrupt(
        scope: StreamRequest['scope'],
        signal: AbortSignal,
    ) {
        return translateApiFailure(async () => {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/interrupt`,
                ...requestScope(scope, signal),
            });
            return { interrupted: response.interrupted };
        });
    }

    async function submitQuestionResponse(
        scope: StreamRequest['scope'],
        request: QuestionAnswerRequest,
        signal: AbortSignal,
    ): Promise<QuestionAnswerResponse> {
        if (request.callId.trim().length === 0) {
            return { kind: 'definitively_rejected' };
        }
        let body: ServerQuestionAnswerRequest;
        switch (request.responseKind) {
            case 'answer':
                body = {
                    call_id: request.callId,
                    answer: copyAnswer(request.answer),
                    answers: null,
                    ...(request.messagePreviewEdit === undefined
                        ? {}
                        : {
                              message_preview_edit: {
                                  recipients: [
                                      ...request.messagePreviewEdit.recipients,
                                  ],
                                  message: request.messagePreviewEdit.message,
                              },
                          }),
                    cancelled: false,
                };
                break;
            case 'structured_answer':
                body = {
                    call_id: request.callId,
                    answer: null,
                    answers: request.answers.map((answer) => ({
                        question_id: answer.question_id,
                        answer: copyAnswer(answer.answer),
                        skipped: answer.skipped,
                    })),
                    cancelled: false,
                };
                break;
            case 'cancel':
                body = {
                    call_id: request.callId,
                    answer: null,
                    answers: null,
                    cancelled: true,
                };
                break;
            default: {
                const exhaustiveRequest: never = request;
                return exhaustiveRequest;
            }
        }
        try {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/question-answers`,
                ...requestScope(scope, signal),
                body,
            });
            return {
                kind: response.already_resolved
                    ? 'already_resolved'
                    : 'accepted',
                cancelled: response.cancelled,
            };
        } catch (error: unknown) {
            return finiteMutationFailure(error);
        }
    }

    async function submitToolDecision(
        scope: StreamRequest['scope'],
        request: ToolDecisionRequest,
        signal: AbortSignal,
    ): Promise<ToolDecisionResponse> {
        let body: ServerToolDecisionRequest;
        switch (request.targetKind) {
            case 'single':
                if (request.callId.trim().length === 0) {
                    return { kind: 'definitively_rejected' };
                }
                body = {
                    call_id: request.callId,
                    call_ids: null,
                    decision: request.decision,
                    always_allow_scope:
                        request.decision === 'always_allow'
                            ? request.alwaysAllowScope
                            : 'conversation',
                    arguments_override:
                        request.decision === 'deny'
                            ? null
                            : (request.argumentsOverride ?? null),
                };
                break;
            case 'batch': {
                const callIds = Array.from(new Set(request.callIds)).sort();
                if (
                    callIds.length < 1 ||
                    callIds.length > 100 ||
                    callIds.some((callId) => callId.trim().length === 0)
                ) {
                    return { kind: 'definitively_rejected' };
                }
                body = {
                    call_id: null,
                    call_ids: callIds,
                    decision: request.decision,
                    always_allow_scope: 'conversation',
                    arguments_override: null,
                };
                break;
            }
            default: {
                const exhaustiveRequest: never = request;
                return exhaustiveRequest;
            }
        }
        try {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/tool-decisions`,
                ...requestScope(scope, signal),
                body,
            });
            return {
                kind: response.already_resolved
                    ? 'already_resolved'
                    : 'accepted',
                decision: response.decision,
            };
        } catch (error: unknown) {
            return finiteMutationFailure(error);
        }
    }

    async function requestInterrupt(
        scope: StreamRequest['scope'],
        signal: AbortSignal,
        requestId?: string,
    ): Promise<InterruptResponse> {
        try {
            const response = await dependencies.getClient().post({
                url: `${RUN_PATH}/interrupt`,
                ...requestScope(scope, signal),
                ...(requestId === undefined ? {} : { requestId }),
            });
            return {
                kind: 'accepted',
                interrupted: response.interrupted,
            };
        } catch (error: unknown) {
            return finiteMutationFailure(error);
        }
    }

    async function updateModel(
        scope: StreamRequest['scope'],
        activeModel: string | null,
    ) {
        return translateApiFailure(() =>
            dependencies.getClient().patch({
                url: `${RUN_PATH}/model`,
                pathParams: {
                    organization_id: scope.organizationId,
                    run_id: scope.runId,
                },
                mode: scope.mode,
                reason: 'v3-agent-run-model',
                body: { active_model: activeModel },
            }),
        );
    }

    async function getMessageFeedback(scope: StreamRequest['scope']) {
        return translateApiFailure(async () => {
            const response = await dependencies.getClient().get({
                url: `${RUN_PATH}/message-feedback`,
                pathParams: {
                    organization_id: scope.organizationId,
                    run_id: scope.runId,
                },
                mode: scope.mode,
                reason: 'v3-agent-run-message-feedback',
            });
            return response.feedback;
        });
    }

    async function updateMessageFeedback(
        scope: StreamRequest['scope'],
        request: Readonly<{
            readonly agentItemId: string;
            readonly label: 'good' | 'bad';
            readonly comment?: string;
        }>,
    ) {
        return translateApiFailure(() =>
            dependencies.getClient().put({
                url: `${RUN_PATH}/message-feedback`,
                pathParams: {
                    organization_id: scope.organizationId,
                    run_id: scope.runId,
                },
                mode: scope.mode,
                reason: 'v3-agent-run-message-feedback',
                body: {
                    agent_item_id: request.agentItemId,
                    label: request.label,
                    // Omitted entirely unless provided: the endpoint reads a
                    // present comment as "replace the saved note".
                    ...(request.comment === undefined
                        ? {}
                        : { comment: request.comment }),
                },
            }),
        );
    }

    return {
        openEventStream,
        getRunSummary,
        updateModel,
        getMessageFeedback,
        updateMessageFeedback,
        sendMessage,
        answerQuestion,
        decideToolApproval,
        interrupt,
        submitQuestionResponse,
        submitToolDecision,
        requestInterrupt,
    };
}

const runApi = createRunApi({
    getClient: getApiClient,
    clock: {
        schedule: (callback, delayMs) => window.setTimeout(callback, delayMs),
        cancel: (handle) => window.clearTimeout(handle),
    },
});

export function openRunEventStream(request: StreamRequest): Promise<Response> {
    return runApi.openEventStream(request);
}

export const sendRunMessage = runApi.sendMessage;
export const fetchRunSummary = runApi.getRunSummary;
export const updateRunModel = runApi.updateModel;
export const fetchRunMessageFeedback = runApi.getMessageFeedback;
export const updateRunMessageFeedback = runApi.updateMessageFeedback;
export const answerRunQuestion = runApi.submitQuestionResponse;
export const decideRunToolApproval = runApi.submitToolDecision;
export const interruptRun = runApi.requestInterrupt;

export function useRunSummary(scope: CandidateRunScope): RunSummaryState {
    const runSummaryKey = [
        'v3-agent-run-summary',
        scope.userId,
        scope.organizationId,
        scope.mode,
        scope.organizationTimeZone,
        scope.profileResolverId,
        'agent-run-read',
        scope.runId,
    ] as const;
    const { data, error } = useSWR<RunSummary>(
        runSummaryKey,
        () => runApi.getRunSummary(scope),
        // The failure is rethrown to the route's own boundary below, which
        // owns its display; without this a server failure would surface
        // twice (global toast plus boundary).
        { onError: () => undefined },
    );
    if (error !== undefined) {
        throw error;
    }
    if (data === undefined) {
        return { kind: 'loading' };
    }
    return { kind: 'ready', summary: data };
}
